/**
 * Accessible Motion Component
 * Automatically handles reduced motion preferences, keyboard navigation, and provides fallbacks
 */

import React, { ReactNode, useRef, useEffect } from 'react';
import { motion, MotionProps } from 'framer-motion';
import { useReducedMotion, useAnimationConfig } from './context';

interface AccessibleMotionProps extends MotionProps {
  children: ReactNode;
  fallback?: MotionProps;
  as?: keyof typeof motion;
  // Accessibility enhancements
  focusable?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  role?: string;
  skipAnimation?: boolean;
  announceChanges?: boolean;
}

/**
 * A wrapper component that automatically respects reduced motion preferences,
 * provides keyboard navigation support, and ensures accessibility compliance
 */
export const AccessibleMotion: React.FC<AccessibleMotionProps> = ({
  children,
  fallback = {},
  as = 'div',
  focusable = false,
  ariaLabel,
  ariaDescribedBy,
  role,
  skipAnimation = false,
  announceChanges = false,
  ...motionProps
}) => {
  const isReducedMotion = useReducedMotion();
  const config = useAnimationConfig();
  const elementRef = useRef<HTMLElement>(null);
  
  // Create the motion component dynamically
  const MotionComponent = motion[as];
  
  // Handle screen reader announcements
  useEffect(() => {
    if (announceChanges && elementRef.current) {
      const element = elementRef.current;
      
      // Create or update aria-live region for announcements
      let liveRegion = document.getElementById('animation-announcements');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'animation-announcements';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        document.body.appendChild(liveRegion);
      }
      
      // Announce animation state changes
      const observer = new MutationObserver(() => {
        if (ariaLabel) {
          liveRegion!.textContent = `${ariaLabel} animation completed`;
        }
      });
      
      observer.observe(element, { attributes: true, attributeFilter: ['style'] });
      
      return () => observer.disconnect();
    }
  }, [announceChanges, ariaLabel]);
  
  // Determine if animations should be disabled
  const shouldDisableAnimation = isReducedMotion || skipAnimation;
  
  // If reduced motion is preferred or animations are disabled, use fallback props
  const finalProps = shouldDisableAnimation ? {
    ...motionProps,
    ...fallback,
    // Override any complex animations with simple opacity changes
    initial: fallback.initial || { opacity: 0 },
    animate: fallback.animate || { opacity: 1 },
    exit: fallback.exit || { opacity: 0 },
    transition: fallback.transition || { duration: config.customTimings.fast },
    // Remove any complex transforms and interactions
    whileHover: undefined,
    whileTap: undefined,
    whileFocus: undefined,
    drag: undefined,
    // Ensure immediate visibility for accessibility
    style: {
      ...motionProps.style,
      opacity: 1,
      transform: 'none',
    },
  } : {
    ...motionProps,
    // Add keyboard focus support for interactive elements
    whileFocus: motionProps.whileFocus || (focusable ? { scale: 1.02 } : undefined),
  };

  // Add accessibility attributes
  const accessibilityProps: any = {
    ref: elementRef,
    ...(ariaLabel && { 'aria-label': ariaLabel }),
    ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
    ...(role && { role }),
    ...(focusable && { tabIndex: 0 }),
  };

  return (
    <MotionComponent {...finalProps} {...accessibilityProps}>
      {children}
    </MotionComponent>
  );
};

/**
 * Pre-configured accessible motion components for common use cases
 */
export const AccessibleMotionDiv = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="div" {...props} />
);

export const AccessibleMotionSection = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="section" {...props} />
);

export const AccessibleMotionArticle = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="article" {...props} />
);

export const AccessibleMotionHeader = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="header" {...props} />
);

export const AccessibleMotionMain = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="main" {...props} />
);

export const AccessibleMotionFooter = (props: Omit<AccessibleMotionProps, 'as'>) => (
  <AccessibleMotion as="footer" {...props} />
);

/**
 * Hook to get accessible animation props
 * Returns simplified props when reduced motion is preferred
 */
export const useAccessibleAnimation = (
  animationProps: MotionProps,
  fallbackProps?: MotionProps
): MotionProps => {
  const isReducedMotion = useReducedMotion();
  const config = useAnimationConfig();
  
  if (isReducedMotion) {
    return {
      ...animationProps,
      ...fallbackProps,
      initial: fallbackProps?.initial || { opacity: 0 },
      animate: fallbackProps?.animate || { opacity: 1 },
      exit: fallbackProps?.exit || { opacity: 0 },
      transition: fallbackProps?.transition || { duration: config.customTimings.fast },
      whileHover: undefined,
      whileTap: undefined,
      drag: undefined,
    };
  }
  
  return animationProps;
};